import React, { Fragment } from 'react';
import { Platform, View } from 'react-native';
import { styled, Sx, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';

import { TariffPlanCodeEnum } from '@constants';
import { IS_WEB, passIf } from '@utils';
import { BaseText } from '../../common/BaseText';
import { Button, ButtonStyleEnum } from '../../common/Button';
import { Icon } from '../../common/Icon';
import { Separator } from '../../common/Separator';

export enum SubscriptionPlanItemStyleEnum {
  Choose = 'choose',
  Promote = 'promote',
}

interface IItemStylePropsStyle {
  itemStyle: SubscriptionPlanItemStyleEnum;
}

function containerStyle(
  style: SubscriptionPlanItemStyleEnum,
  width: number,
): SxProp {
  switch (style) {
    case SubscriptionPlanItemStyleEnum.Promote:
      return {
        bg: '$primary300',
        my: ['$6', null, null, '$0'],
        width: [null, null, null, width + 30],
      };

    default:
      return {
        bg: '$background10',
        my: ['$6', null, null, '$14'],
        width: [null, null, null, width],
      };
  }
}

interface IContainerStyleProps extends IItemStylePropsStyle {
  itemWidth: number;
}

const Container = styled(View)((props: IContainerStyleProps) => ({
  p: '$8',
  pt: '$9',
  justifyContent: 'space-between',
  borderRadius: 26,
  ...containerStyle(props.itemStyle, props.itemWidth),
}));

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
});

const Title = styled(BaseText)({
  variant: 'text.xxlarge',
  color: '$heading600',
  fontWeight: 'semi',
});

interface IPriceTextStyleProps {
  length: number;
  isCrossedOut?: boolean;
}

const PriceText = styled(BaseText)((props: IPriceTextStyleProps) => ({
  color: '$heading800',
  fontWeight: 'semi',
  variant: props.length > 8 ? 'text.xxlarge' : 'text.xbig',
}));

const RegularPriceText = styled(BaseText)(
  (props: IPriceTextStyleProps) => ({
    color: '$text400',
    fontWeight: 'semi',
    variant: props.length > 8 ? 'text.medium' : 'text.xlarge',
    textDecorationLine: passIf(props.isCrossedOut, 'line-through'),
  }),
);

const PerTimeText = styled(BaseText)({
  variant: 'text.medium',
  color: '$text400',
});

const BaseLineContainer = styled(View)({
  alignItems: 'baseline',
});

const BadgeContainer = styled(View)({
  py: '$2',
  px: '$4',
  bg: '$primary90',
  borderRadius: 14,
});

const BadgeText = styled(BaseText)({
  color: '$error400',
  fontWeight: 'semi',
  fontSize: ['small', 'medium'],
  lineHeight: ['small', 'medium'],
});

interface IFeaturesContainerStyleProps {
  columnCount: number;
}

const FeaturesContainer = styled(View)(
  (props: IFeaturesContainerStyleProps) =>
    Platform.select<Sx>({
      default: {},
      web: {
        // columnCount is web only prop
        columnCount: [null, null, null, props.columnCount],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        display: [null, null, null, 'inline'],
      },
    }),
);

const FeatureItemContainer = styled(View)({
  pt: '$7',
});

const FeatureItemText = styled(BaseText)({
  variant: 'text.medium',
  color: '$text400',
});

const FeatureBadgeContainer = styled(View)({
  py: '$2',
  px: '$4',
  bg: '$primary90',
  borderRadius: 6,
});

const FeatureBadgeText = styled(BaseText)({
  variant: 'text.xxsmall',
  color: '$text200',
  fontWeight: 'semi',
});

const PromoBadgeContainer = styled(FeatureBadgeContainer)({
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: 12,
});

const PromoBadgeText = styled(FeatureBadgeText)({
  variant: 'text.small',
});

const IconContainer = styled(View)({
  mr: '$4',
  justifyContent: 'center',
  alignItems: 'center',
  size: 20,
  borderRadius: 10,
  backgroundColor: '$primary90',
});

const SubtitleText = styled(BaseText)({
  variant: 'text.medium',
  color: '$text100',
});

interface ISubscriptionPlanItemFeatureVideo {
  name: string;
  array: string[];
}

export interface ISubscriptionPlanItemFeature {
  name: string;
  badge?: string;
  videos?: ISubscriptionPlanItemFeatureVideo;
}

interface IItem {
  title: string;
  regularPrice: string;
  currentPrice: string;
  time?: string | null;
  promo?: string | null;
  badge?: string | null;
  features?: string[];
  code: TariffPlanCodeEnum;
  subtitle?: string;
}

export interface ISubscriptionPlanItemProps {
  item: IItem;
  onPress?(item: TariffPlanCodeEnum): void;
  buttonText?: string;
  style?: SubscriptionPlanItemStyleEnum;
  width?: number;
}

export const SubscriptionPlanItem =
  observer<ISubscriptionPlanItemProps>(
    ({
      item,
      onPress,
      buttonText,
      style = SubscriptionPlanItemStyleEnum.Choose,
      width = 300,
    }) => {
      const buttonStyle =
        style === SubscriptionPlanItemStyleEnum.Promote
          ? ButtonStyleEnum.Outline
          : ButtonStyleEnum.Flat;

      const regularPriceLength = item.regularPrice?.length ?? 0;
      const currentPriceLength = item.currentPrice?.length ?? 0;
      const featColumnCount = width > 300 ? 2 : 1;

      const hasOnPress = !!onPress;

      return (
        <Container itemWidth={width} itemStyle={style}>
          <View>
            {IS_WEB && !!item.badge && (
              <BaseLineContainer>
                <BadgeContainer>
                  <BadgeText>{item.badge}</BadgeText>
                </BadgeContainer>
              </BaseLineContainer>
            )}
            <RowContainer>
              <BaseText numberOfLines={1}>
                {!!item.regularPrice && (
                  <Fragment>
                    <RegularPriceText
                      length={regularPriceLength}
                      isCrossedOut
                    >
                      {item.regularPrice}
                    </RegularPriceText>
                    <Separator size="$4" vertical />
                  </Fragment>
                )}

                <PriceText length={currentPriceLength}>
                  {item.currentPrice}
                </PriceText>

                {!!item.time && (
                  <PerTimeText> / {item.time}</PerTimeText>
                )}
              </BaseText>
            </RowContainer>

            {!!item?.subtitle && (
              <SubtitleText>{item?.subtitle}</SubtitleText>
            )}

            {!!item?.promo && (
              <Fragment>
                <Separator />
                <RowContainer>
                  <PromoBadgeContainer>
                    <Icon
                      name="star-light"
                      color="$text200"
                      size={16}
                    />
                    <Separator size="$2" vertical />
                    <PromoBadgeText>{item.promo}</PromoBadgeText>
                  </PromoBadgeContainer>
                </RowContainer>
              </Fragment>
            )}

            <Separator size="$7" />
            <Title>{item.title}</Title>

            <FeaturesContainer columnCount={featColumnCount}>
              {item.features?.map((feature, index) => (
                <FeatureItemContainer key={index}>
                  {feature && (
                    <RowContainer>
                      <IconContainer>
                        <Icon
                          name="done-light"
                          color="$primary300"
                          size={16}
                        />
                      </IconContainer>
                      <FeatureItemText>{feature}</FeatureItemText>
                    </RowContainer>
                  )}
                </FeatureItemContainer>
              ))}
            </FeaturesContainer>
          </View>

          <View>
            <Separator size="$8" />
            {hasOnPress && (
              <Button
                buttonStyle={buttonStyle}
                onPress={() => onPress(item.code)}
              >
                {buttonText}
              </Button>
            )}
          </View>
        </Container>
      );
    },
  );
